import { Space, Splitter, Tag } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getProblems, getProblemTestCases } from '../../store/actions/InterviewActions';
import FilterBar from './FilterBar';
import QuestionDetails from './QuestionDetails';
import './QuestionTab.scss';
import QuestionTable from './QuestionTable';

export const testCaseDifficultyMap = {
  EZ: 'Easy',
  MD: 'Medium',
  HD: 'Hard',
  UN: 'Unknown',
};

// 🔹 Main Component
const QuestionTab = ({ onAddQuestionTab }) => {
  const dispatch = useDispatch();

  const problems = useSelector(state => state.problem.problems);
  const test_cases = useSelector(state => state.problem.test_cases);
  const tags = useSelector(state => state.problem.tags);
  const problemDetails = useSelector(state => state.problem.problemDetails);

  const addedProblemIds = useMemo(
    () => new Set((problemDetails || []).map(p => p.problem_details?.id).filter(Boolean)),
    [problemDetails],
  );

  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [filters, setFilters] = useState({ text: '', difficulty: '', tags: [] });
  const [appliedSearchText, setAppliedSearchText] = useState('');

  useEffect(() => {
    // if (selectedQuestion && !problem?.id) {
    const urlparams = new URLSearchParams(window.location.search);
    const interview_id = urlparams.get('interview_id');
    dispatch(getProblems());
    // dispatch(getProblemsTags())
    // }
  }, []);

  useEffect(() => {
    if (!selectedQuestion) return;
    dispatch(getProblemTestCases(selectedQuestion.id));
  }, [selectedQuestion]);

  const tagOptions = useMemo(
    () => Object.entries(tags || {}).map(([id, name]) => ({ value: id, label: name })),
    [tags],
  );

  const data = useMemo(() => {
    let list =
      problems
        ?.map(p => p) // extract problem_details
        .filter(Boolean) || []; // remove null/undefined if any
    if (appliedSearchText) {
      list = list.filter(p =>
        (p.title || '').toLowerCase().includes(appliedSearchText.toLowerCase()),
      );
    }

    if (filters.difficulty) {
      list = list.filter(p => p.difficulty === filters.difficulty);
    }

    if (filters.tags.length) {
      list = list.filter(p => p.tags?.some(t => filters.tags.includes(t)));
    }

    list = list.filter(p => p.publication_status === 'PB');

    return list.map(p => ({ ...p, key: p.id }));
  }, [problems, filters, appliedSearchText]);
  const columns = [
    { title: 'Question', dataIndex: 'title', key: 'title', ellipsis: true },
    {
      title: 'Tags',
      key: 'tags',
      dataIndex: 'tags',
      render: ids => (
        <Space wrap>
          {ids?.slice(0, 2).map(id => (
            <Tag key={id}>{tags[id]}</Tag>
          ))}
          {ids?.length > 2 && <Tag>+{ids.length - 2}</Tag>}
        </Space>
      ),
    },
    {
      title: 'Difficulty',
      key: 'difficulty',
      dataIndex: 'difficulty',
      render: d => (
        <Tag color={d === 'EZ' ? 'green' : d === 'MD' ? 'orange' : 'red'}>
          {testCaseDifficultyMap[d]}
        </Tag>
      ),
    },
  ];

  return (
    <>
      <FilterBar
        {...{ filters, setFilters, appliedSearchText, setAppliedSearchText, tagOptions }}
      />
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
          <Splitter
            size={0}
            style={{ height: '100%' }}
          >
            <Splitter.Panel
              size={`${selectedQuestion ? '60%' : '100%'}`}
              resizable={false}
              style={{ overflow: 'auto' }}
            >
              <div style={{ paddingLeft: 16, paddingRight: 16, paddingBottom: 40 }}>
                <QuestionTable
                  data={data}
                  columns={columns}
                  selectedQuestion={selectedQuestion}
                  onSelect={setSelectedQuestion}
                />
              </div>
            </Splitter.Panel>
            <Splitter.Panel style={{ overflow: 'auto' }}>
              {selectedQuestion ? (
                <QuestionDetails
                  question={selectedQuestion}
                  testCases={test_cases}
                  onClose={() => setSelectedQuestion(null)}
                  tags={tags}
                  onAddQuestionTab={onAddQuestionTab}
                  isAlreadyAdded={addedProblemIds.has(selectedQuestion.id)}
                />
              ) : (
                ''
              )}
            </Splitter.Panel>
          </Splitter>
        </div>
      </div>
    </>
  );
};

export default QuestionTab;
